import { Stack, Typography } from '@mui/material';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router';
import RecommendationCreate from '/@/components/recommendation-create';
import FieldSelect from '/@/components/recommendation-create/bayer/fields-step';
import Spinner from '/@/components/spinner';
import useUnitProvider from '/@/core/context/unit';
import settings from '/@/core/settings';
import { useFieldById } from '/@/hooks/fields';
import { useDefaultCropConfig } from '/@/hooks/recommendation';
import { _ } from '/@/locale';
import { getCropConfig } from '/@/store/private-data/recommendations/selectors';
import { updateActiveRecommendation } from '/@/store/recommendations/actions';
import { getActiveRecommendation } from '/@/store/recommendations/selectors';
import { getAppInstance, getSessionBrands } from '/@/store/session/selectors';
import type { EVENTS as SEED_RECOMMENDATION_EVENTS } from '/@/store/tracking/events/seed-recommendation';
import { mpTrackEvent } from '/@/store/tracking/mixpanel-events';
import type { Field } from '/@/types/field';
import type { Step } from '/@/types/recommendation-create';
import { formatDate } from '/@/utils';
import { reverse } from '/@/utils/router';
import EnvironmentStep from '/@/views/recommendations/v2/steps/environment';
import HybridsStep from '/@/views/recommendations/v2/steps/hybrids';
import FieldSummary from '/@/views/recommendations/v2/steps/hybrids/field-summary';
import { useFieldRisks } from '/@/views/recommendations/v2/steps/hybrids/use-field-risks';
import { useHybrids } from '/@/views/recommendations/v2/steps/hybrids/use-hybrids';
import ManagementStep from '/@/views/recommendations/v2/steps/management';
import { useRecommendationResultData } from '../recommendation-aio/hooks';
import { useSaveRecommendation } from '../use-save-recommendation';

type SeedRecommendationEntrySource = Parameters<
    (typeof SEED_RECOMMENDATION_EVENTS)['Seed recommendation began']
>[0]['entry_source'];

function RecommendationFieldSelect({ crop = 'corn' }: { crop?: 'corn' | 'soy' }) {
    const { search } = useLocation();
    const query = new URLSearchParams(search);
    const entrySource = query.get('entrySource') as SeedRecommendationEntrySource;
    // Entry points that already know the field (field list, crop monitoring, the RV modal with a
    // field picked, …) pass it in and land straight on the environment step.
    const preselectedFieldId = Number(query.get('fieldId')) || undefined;
    const dispatch = useDispatch();
    const [activeStep, setActiveStep] = useState<number>(preselectedFieldId ? 1 : 0);
    const [selectedField, setSelectedField] = useState<Field | undefined>(undefined);

    // The field list is fetched with a reduced field set (no country, no zones), so the
    // crop config and the environment step need the full record.
    const fieldId = selectedField?.id ?? preselectedFieldId;
    const { data: field } = useFieldById({ id: fieldId as number }, { enabled: !!fieldId });

    // Adopt a preselected field as the selection, so going back to step 1 shows it picked.
    useEffect(() => {
        if (!selectedField && field?.id === preselectedFieldId) setSelectedField(field);
    }, [field, preselectedFieldId, selectedField]);

    const appInstance = useSelector(getAppInstance);
    const defaultCropConfig = useDefaultCropConfig({ crop, field });
    const { risks } = useFieldRisks(field);
    const recommendation = useSelector(getActiveRecommendation);
    const cropConfig = getCropConfig(recommendation, crop);
    const units = useUnitProvider();
    const navigate = useNavigate();
    const sessionBrands = useSelector(getSessionBrands);

    // Everything the save payload needs. These are queries and memos only — no defaulting
    // effects — so mounting them at the view level is safe (unlike useRecommendationEditData,
    // which rewrites the environment on mount).
    const { featuredProducts, otherProducts } = useHybrids({ field, crop });
    const mappedProducts = useMemo(
        () =>
            [...featuredProducts, ...otherProducts].reduce<Record<number, unknown>>((byId, product) => {
                byId[product.id] = product;
                return byId;
            }, {}),
        [featuredProducts, otherProducts]
    );
    // `getCropConfig` returns `{}` before a recommendation exists, so normalise the fields
    // `useRecommendationResultData` reads rather than just checking for a falsy config.
    const cropConfigForSave = useMemo(
        () => ({
            ...cropConfig,
            crop: cropConfig?.crop || crop,
            products: cropConfig?.products || [],
        }),
        [cropConfig, crop]
    );
    const { productsWithAPIData, pestControlPlans, catalogBrands } = useRecommendationResultData({
        field,
        crop,
        cropConfig: cropConfigForSave,
        mappedProducts,
    });
    const { save, savedRecommendation } = useSaveRecommendation();

    const saveArgs = {
        recommendation,
        productsWithAPIData,
        brand: cropConfig ? sessionBrands[cropConfig.crop]?.[0] : undefined,
        crop,
        pestControlPlans,
        field,
        catalogBrands,
        cropConfig: cropConfigForSave,
    };

    // Keep a draft from the moment there is something worth keeping, so abandoning the wizard
    // doesn't throw the work away. `Terminar` promotes the same record to `read_only`.
    const hasSelection = !!cropConfig?.products?.length;
    // biome-ignore lint/correctness/useExhaustiveDependencies: keyed on reaching the last step
    useEffect(() => {
        if (activeStep < 3 || !hasSelection || !recommendation) return;
        save({ ...saveArgs, savedRecommendation, status: 'draft', notify: false });
    }, [activeStep, hasSelection]);

    const handleFinish = async () => {
        const saved = await save({ ...saveArgs, savedRecommendation, status: 'read_only' });
        if (saved?.id) {
            navigate(
                reverse('recommendations.RecommendationResultV2', { params: { recommendationId: saved.id, crop } })
            );
        }
    };

    const environmentSummary = useMemo(() => {
        if (!cropConfig?.environment) return undefined;
        const value = units.toYield(cropConfig.environment, crop, 0);
        return (
            <Stack p={1} borderRadius='4px' sx={{ backgroundColor: (theme) => theme.palette.grey[100] }}>
                <Typography variant='subtitle1' fontWeight={700} color='primary'>
                    {`${value.localeValue} ${value.unit}`}
                </Typography>
                {cropConfig.plantingDate && (
                    <Typography variant='caption' color='primary.mui.inactive'>
                        {_('Fecha de siembra: {{date}}', {
                            date: formatDate(new Date(cropConfig.plantingDate)),
                        })}
                    </Typography>
                )}
            </Stack>
        );
    }, [cropConfig?.environment, cropConfig?.plantingDate, crop, units]);

    const hybridsSummary = useMemo(() => {
        const selectedIds: number[] = cropConfig?.products || [];
        if (!selectedIds.length) return undefined;
        // Selection order, not catalogue order, so the recap matches the cards.
        const names = selectedIds
            .map((id) => (mappedProducts[id] as { catalogName?: string } | undefined)?.catalogName)
            .filter(Boolean);
        if (!names.length) return undefined;
        return (
            <Stack p={1} borderRadius='4px' sx={{ backgroundColor: (theme) => theme.palette.grey[100] }}>
                <Typography variant='subtitle1' fontWeight={700} color='primary'>
                    {names.join(' - ')}
                </Typography>
            </Stack>
        );
    }, [cropConfig?.products, mappedProducts]);

    // Crop config handed over by another flow (the density recommendation), overriding the
    // zone defaults. Seeded here rather than left to the environment step: React runs child
    // effects before parent ones, so a step applying them first would just be overwritten by
    // this seed.
    // biome-ignore lint/correctness/useExhaustiveDependencies: derived from the URL, fixed per mount
    const handoffCropConfig = useMemo(() => {
        const plantingDate = query.get('planting_date');
        const climatePattern = Number(query.get('climate_pattern'));
        const soilMoisture = Number(query.get('soil_moisture'));
        return {
            ...(plantingDate ? { plantingDate } : {}),
            ...(Number.isFinite(climatePattern) && query.get('climate_pattern') ? { climatePattern } : {}),
            ...(Number.isFinite(soilMoisture) && query.get('soil_moisture') ? { soilMoisture } : {}),
        };
    }, [search]);

    // Seed the same `activeRecommendation` store slice the recommendation-edit page
    // uses, so the environment step can reuse its derivation cascade. Guarded per field:
    // `defaultCropConfig`'s identity changes while its own queries are still resolving, and
    // re-seeding would throw away whatever the user has already configured on step 2.
    const seededFieldId = useRef<number | undefined>(undefined);
    useEffect(() => {
        if (!field || !defaultCropConfig || seededFieldId.current === field.id) return;
        seededFieldId.current = field.id;
        dispatch(
            updateActiveRecommendation({
                appInstance: appInstance.id,
                field: String(field.id),
                crops: [{ ...defaultCropConfig, ...handoffCropConfig }],
            })
        );
    }, [appInstance.id, dispatch, field, defaultCropConfig, handoffCropConfig]);

    useEffect(() => {
        return () => {
            dispatch(updateActiveRecommendation(undefined));
        };
    }, [dispatch]);

    // The recommendation-edit page fired this on mount; the wizard no longer
    // navigates there, so the event moves to the step that replaces it.
    useEffect(() => {
        if (activeStep === 1) mpTrackEvent('Seed recommendation began', { crop, entry_source: entrySource });
    }, [activeStep, crop, entrySource]);

    const steps: Step[] = [
        {
            sidebarName: _('Selección de lote'),
            recommendationType: _('Recomendación de semilla'),
            title: _('Elegí el lote para generar la recomendación'),
            subtitle: _('Seleccioná un lote para obtener una recomendación ajustada a sus condiciones específicas.'),
            content: (
                <FieldSelect
                    field={selectedField}
                    setField={(field) => {
                        setSelectedField(field);
                    }}
                />
            ),
            sidebarSummary: field ? <FieldSummary field={field} risks={risks} /> : undefined,
            onClickNextDisabled: () => selectedField === undefined,
            onClickBackDisabled: () => true,
        },
        {
            sidebarName: _('Configurar ambiente'),
            recommendationType: _('Recomendación de híbrido'),
            title: _('Configurá el ambiente del lote'),
            subtitle: _(
                'Con modelos de simulación, ensayos y rindes históricos de la zona te mostramos la situación más probable para este lote. Ajustá las variables y el ambiente con lo que sabés del lote.'
            ),
            content: field ? (
                <EnvironmentStep field={field} crop={crop} />
            ) : (
                <Stack alignItems='center' py={6}>
                    <Spinner />
                </Stack>
            ),
            sidebarSummary: environmentSummary,
        },
        {
            sidebarName: _('Híbridos recomendados'),
            recommendationType: _('Recomendación de híbrido'),
            title: _('Elegí los híbridos para este lote'),
            subtitle: _(
                'Híbridos recomendados según el ambiente y las condiciones del lote. Seleccioná {{count}} para ver las recomendaciones específicas.',
                { count: settings.maxProductSelection }
            ),
            content: field ? (
                <HybridsStep field={field} crop={crop} />
            ) : (
                <Stack alignItems='center' py={6}>
                    <Spinner />
                </Stack>
            ),
            sidebarSummary: hybridsSummary,
            onClickNextDisabled: () => !cropConfig?.products?.length,
        },
        {
            sidebarName: _('Manejo recomendado'),
            recommendationType: _('Recomendación de híbrido'),
            title: _('Conocé el manejo recomendado'),
            subtitle: _(
                'Densidad, fenología y fertilización para este lote, según el ambiente y los híbridos seleccionados.'
            ),
            content: field ? (
                <ManagementStep field={field} crop={crop} onEditEnvironment={() => setActiveStep(1)} />
            ) : (
                <Stack alignItems='center' py={6}>
                    <Spinner />
                </Stack>
            ),
            onClickNextText: _('Terminar'),
            onClickNext: handleFinish,
            onClickNextDisabled: () => !hasSelection,
        },
    ];
    return RecommendationCreate({ steps, activeStep, setActiveStep });
}

export default RecommendationFieldSelect;
